import { Spin } from 'antd';
import { useSelector } from 'react-redux';
import { Box, Grid } from '@mui/material';
import { decodeQueryParamData } from 'utils/utils';
import TabPanel from 'components/TabPanel/TabPanel';
import { existTabItems } from 'pages/globals/global';
import { formatPayloadForLynTask } from 'utils/apiUtils';
import { useParams, useSearchParams } from 'react-router-dom';
import SolutionCatalogIcon from 'assets/icons/solutions-catalog';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ListViewLeavePage from './CustomComponents/ListViewLeavePage';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import ComptrollerPageHeader from 'components/header/ComptrollerPageHeader';
import { useGetAllIdentityCaseMutation, useGetSolutionNameMutation } from 'store/features/process/processApi';

const permissionsObject = {
	read: true,
	write: true,
	edit: true,
	delete: false,
};

const headMetaData = {
	meta: {
		staticFormUrl: '/static/leave-combine',
	},
};
const LeaveManagement = ({ from = 'identity', forceActiveTab }) => {
	const params = useParams();
	const [searchParams] = useSearchParams();

	const [refresh, setRefresh] = useState(false);
	const [leaveData, setLeaveData] = useState([]);
	const [selectedTab, setSelectedTab] = useState(0);

	const { profileData } = useSelector((state) => state?.profile);

	const [getLeaveDetails, { data: leaveDetailsData, isLoading: isLeaveDetailsDataLoading }] = useSendControlDataMutation();
	const [getAllIdentityCase, { data: getAllIdentityCaseData, isLoading: isDataLoading }] = useGetAllIdentityCaseMutation();
	const [GetSolutionName, { data: processDetails }] = useGetSolutionNameMutation();

	const selectTabHandler = (event, newValue) => {
		setSelectedTab(newValue);

		const tabName = TabsItem?.[newValue]?.label?.toLowerCase()?.replaceAll('-', '');

		const newSearchParams = new URLSearchParams(window.location.search);
		newSearchParams.set('tab', tabName);
		window.history.replaceState(null, '', `${window.location.pathname}?${newSearchParams.toString()}`);
	};

	console.log(getAllIdentityCaseData, leaveDetailsData, 'data');

	const getTabsWithData = () => {
		if (!getAllIdentityCaseData || getAllIdentityCaseData.length === 0) {
			return []; // Don't show any tabs if no data yet
		}

		return existTabItems.filter((tab) => {
			if (tab.data === 'All') {
				return getAllIdentityCaseData.length > 0; // Only show 'All' tab if there's actual data
			}
			if (tab.data === 'Worklist') {
				return getAllIdentityCaseData.some(
					(item) => item?.activationStatus === 'W-I-P' && item?.recordStatus === 'parked' && profileData?.userId === item?.approverUserId[0]
				);
			}
			if (tab.data === 'W-I-P') {
				// For W-I-P tab, exclude items that are in worklist (parked + user is approver)
				const data = getAllIdentityCaseData.some(
					(item) =>
						item?.activationStatus === 'W-I-P' && !(item?.recordStatus === 'parked' && profileData?.userId === item?.approverUserId[0])
				);
				// console.log(data, 'data1122');

				return data;
			}
			if (tab.data === 'Draft') {
				return getAllIdentityCaseData.some((item) => item?.activationStatus === 'draft');
			}

			// Check if there's at least one item with this activation status
			return getAllIdentityCaseData.some((item) => item?.activationStatus === tab.data);
		});
	};

	const refetchData = useCallback(
		() =>
			getAllIdentityCase({
				companyCode: '24155',
				operation: 'get-bulk',
				processCode: '10127',
				suffixCode: '',
			}),
		[refresh]
	);

	const TabsItem = useMemo(() => getTabsWithData(), [getAllIdentityCaseData, profileData]);

	const paramsData = useMemo(() => {
		if (params?.id) {
			return decodeQueryParamData(params?.id);
		}
	}, []);

	useEffect(() => {
		// Don't try to set a tab if we don't have any tabs yet.
		if (!TabsItem || TabsItem.length === 0) {
			return;
		}

		let tabToActivate = -1;
		const findTabIndexByLabel = (label) => {
			if (!label) return -1;
			return TabsItem.findIndex((item) => item?.label?.toLowerCase()?.replaceAll('-', '') === label.toLowerCase().replaceAll('-', ''));
		};

		// Priority 3: Check for forceActiveTab prop
		if (forceActiveTab) {
			tabToActivate = findTabIndexByLabel(forceActiveTab);
		}

		// Priority 2: Check for URL search param (if forceActiveTab not found or not provided)
		if (tabToActivate === -1) {
			const activeTabFromUrl = searchParams.get('tab');
			if (activeTabFromUrl) {
				tabToActivate = findTabIndexByLabel(activeTabFromUrl);
			}
		}

		// If we found a valid tab from the prop or URL, set it.
		if (tabToActivate > -1) {
			setSelectedTab(tabToActivate);
		}
		// Optional: If the current selectedTab becomes invalid (e.g., data changes and tab disappears), reset to 0.
		else if (selectedTab >= TabsItem.length) {
			setSelectedTab(0);
		}
	}, [TabsItem, forceActiveTab, searchParams]);

	useEffect(() => {
		getLeaveDetails(
			formatPayloadForLynTask(
				{
					query: 'get-leave-types',
				},
				'leave-controller',
				'10127'
			)
		);
		refetchData();
	}, []);

	useEffect(() => {
		GetSolutionName({
			code: 10127,
		});
	}, []);

	useEffect(() => {
		if (leaveDetailsData?.responseData?.length > 0) {
			setLeaveData(leaveDetailsData?.responseData ?? []);
		}
	}, [leaveDetailsData]);

	const getFilteredData = useMemo(() => {
		if (!getAllIdentityCaseData || !TabsItem[selectedTab]) {
			return [];
		}
		const currentTabValue = TabsItem[selectedTab].data;

		if (currentTabValue === 'All') {
			return getAllIdentityCaseData?.map((item) => ({
				...item?.data,
				recordStatus: item?.recordStatus,
				activationStatus: item?.activationStatus,
				_id: item?._id,
			}));
		}

		if (currentTabValue === 'Worklist') {
			return getAllIdentityCaseData
				.filter(
					(item) => item?.activationStatus === 'W-I-P' && item?.recordStatus === 'parked' && profileData?.userId === item?.approverUserId[0]
				)
				.map((item) => ({ ...item?.parkedData, recordStatus: item?.recordStatus, activationStatus: item?.activationStatus, _id: item?._id }));
		}

		if (currentTabValue === 'W-I-P') {
			return getAllIdentityCaseData
				.filter(
					(item) =>
						item?.activationStatus === 'W-I-P' && !(item?.recordStatus === 'parked' && profileData?.userId === item?.approverUserId[0])
				)
				.map((item) => {
					if (item?.parkedData) {
						return { ...item?.parkedData, recordStatus: item?.recordStatus, activationStatus: item?.activationStatus, _id: item?._id };
					} else {
						return { ...item?.data, recordStatus: item?.recordStatus, activationStatus: item?.activationStatus, _id: item?._id };
					}
				});
		}

		if (currentTabValue === 'Draft') {
			return getAllIdentityCaseData
				.filter((item) => item?.activationStatus === 'draft')
				.map((item) => ({ ...item?.data, recordStatus: item?.recordStatus, activationStatus: item?.activationStatus, _id: item?._id }));
		}

		return getAllIdentityCaseData
			.filter((item) => item?.activationStatus === currentTabValue)
			.map((item) => ({ ...item?.data, recordStatus: item?.recordStatus, activationStatus: item?.activationStatus, _id: item?._id }));
	}, [getAllIdentityCaseData, selectedTab, profileData, TabsItem]);

	return (
		<Grid container rowSpacing={4.5}>
			<Grid item xs={12}>
				<Grid item xs={12}>
					{from === 'identity' && (
						<ComptrollerPageHeader
							tabsItem={TabsItem}
							selectedTab={selectedTab}
							selectTabHandler={selectTabHandler}
							title={paramsData?.processName ?? ''}
							createBtn={false}
							image={<SolutionCatalogIcon color="#fff" width="20px" height="20px" />}
						/>
					)}

					<Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%' }}>
						{TabsItem?.map((tab, index) => (
							<TabPanel key={tab.label} value={selectedTab} index={index}>
								<Box sx={{ background: '#fff', padding: '20px', borderRadius: '4px' }}>
									{index === 0 ? (
										// Content for the first tab ('Active')
										<Spin spinning={isLeaveDetailsDataLoading} style={{ maxHeight: '100%' }}>
											<ListViewLeavePage
												isLoading={isDataLoading}
												listData={leaveData}
												selectedTabName={tab.data} // Use the correct tab data string
												currentRoute="v6"
												processDetails={processDetails}
												permissionsObject={permissionsObject}
												headMetaData={headMetaData}
												expectedURLToRedirect="/static/lease-record-management-v6"
												setRefresh={setRefresh}
												refetchData={refetchData}
											/>
										</Spin>
									) : (
										<ListViewLeavePage
											isLoading={isDataLoading}
											listData={getFilteredData}
											selectedTabName={tab.data} // Use the correct tab data string
											currentRoute="v6"
											processDetails={processDetails}
											permissionsObject={permissionsObject}
											headMetaData={headMetaData}
											expectedURLToRedirect="/static/lease-record-management-v6"
											setRefresh={setRefresh}
											refetchData={refetchData}
										/>
									)}
								</Box>
							</TabPanel>
						))}
					</Box>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default LeaveManagement;
